import { Collapse, Paper } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useParams, useSearch } from "@tanstack/react-router"
import dayjs from "dayjs"
import type { FC, ReactElement } from "react"
import { useCallback, useMemo, useState } from "react"
import { useMutation, useQuery } from "urql"

import type { SignedUrl } from "../ProductForm/types"

import { Header } from "./components/Header"
import { LabelInformationContent } from "./LabelInformationContent"
import { LabelInformationDialog } from "./LabelInformationDialog"
import type { LabelInformation, LabelInformationFormData } from "./types"
import { mapSignedUrlsToPDFSizePattern } from "./types"
import { ViewLabelsDialog } from "./ViewLabelsDialog"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { GetFileInfoQuery, GetFileInfoQueryVariables } from "@/graphql/codegen/graphql"
import {
  FieldGroupEnum,
  GetFileInfoDocument,
  ProductLabelInfoDocument,
  ProductUpdateDocument,
  ReferenceTableName,
} from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { useImageUpload } from "@/hooks/useImageUpload"
import { usePDFUpload } from "@/hooks/usePDFUpload"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { NotificationType } from "@/schemas/notification"
import { getLatestSignedUrls } from "@/screens/Products/routeLoader"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const getLatestImageSignedUrl = (signedUrls: SignedUrl[]): SignedUrl => {
  return getLatestSignedUrls(
    signedUrls.filter(
      (signedUrl: SignedUrl) =>
        signedUrl.file?.originalName?.includes(".jpg") ||
        signedUrl.file?.originalName?.includes(".png") ||
        signedUrl.file?.originalName?.includes(".jpeg") ||
        signedUrl.file?.originalName?.includes(".gif")
    )
  ).sort((a, b) => {
    const aDate = dayjs(a.file?.createdOn)
    const bDate = dayjs(b.file?.createdOn)
    return bDate.diff(aDate) // Sort descending (newest first)
  })[0] as SignedUrl
}

interface LabelInformationContainerProps {
  isPastProductVersion: boolean
}

export const LabelInformationContainer: FC<LabelInformationContainerProps> = ({
  isPastProductVersion,
}): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const { productId } = useParams({ from: "/app/product/$productId/edit/_layout/information" })
  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })

  const [{ data }, getProductLabelInfo] = useQuery({
    query: ProductLabelInfoDocument,
    variables: { productId: Number(productId), version },
  })

  const [{ data: fileUploadData }, refetchFileUploadData] = useQuery<GetFileInfoQuery, GetFileInfoQueryVariables>({
    query: GetFileInfoDocument,
    variables: {
      fileInfoInput: {
        referenceTableName: ReferenceTableName.Product,
        referenceId: productId.toString(),
        fieldGroups: [FieldGroupEnum.ProductLabel],
      },
    },
  })

  const { removeSingleFile } = useFilesRemove(refetchFileUploadData)

  const getFileInfo = useCallback(async (): Promise<{ data?: GetFileInfoQuery }> => {
    const result = await queryClient
      .query<GetFileInfoQuery, GetFileInfoQueryVariables>(
        GetFileInfoDocument,
        {
          fileInfoInput: {
            referenceTableName: ReferenceTableName.Product,
            referenceId: productId.toString(),
            fieldGroups: [FieldGroupEnum.ProductLabel],
          },
        },
        { requestPolicy: "network-only" }
      )
      .toPromise()

    return { data: result.data }
  }, [productId])

  const { uploadFile: uploadPDFFile } = usePDFUpload({
    referenceTableName: ReferenceTableName.Product,
    getReferenceId: () => productId.toString(),
  })
  const { uploadFile: uploadImageFile } = useImageUpload({
    referenceTableName: ReferenceTableName.Product,
  })
  const [, updateProduct] = useMutation(ProductUpdateDocument)

  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const fileData = useMemo(() => {
    if (!fileUploadData) return []

    if (fileUploadData.fileUpload.getFileInfo.__typename === "GetFileInfoFailure") {
      return []
    }

    return fileUploadData.fileUpload.getFileInfo.signedUrls
  }, [fileUploadData])

  const labelInfo = useMemo((): LabelInformation => {
    if (data?.product.get.__typename === "ProductGetSuccess") {
      return {
        isPrivateLabel: data.product.get.product.privateLabel ?? false,
        isPrintedBySupplier: data.product.get.product.labelPrintedBySupplier ?? null,
        isAppliedBySupplier: data.product.get.product.labelAppliedBySupplier ?? null,
      }
    }
    return {
      isPrivateLabel: false,
      isPrintedBySupplier: null,
      isAppliedBySupplier: null,
    }
  }, [data])

  const [expanded, setExpanded] = useState(true)
  const [openUpdateLabelInfo, setOpenUpdateLabelInfo] = useState(false)
  const [openViewLabels, setOpenViewLabels] = useState(false)
  const files = useMemo(
    () => ({
      pdfs: mapSignedUrlsToPDFSizePattern(fileData?.filter(({ file }) => file?.originalName?.includes(".pdf")) ?? []),
      image: getLatestImageSignedUrl(fileData ?? []),
    }),
    [fileData]
  )

  const hasFiles = useMemo(
    () => Boolean(Object.keys(files.pdfs)?.length) || Boolean(files.image),
    [files.pdfs, files.image]
  )

  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), [])
  const handleCloseUpdateLabelInfo = useCallback(() => setOpenUpdateLabelInfo(false), [])
  const handleCloseViewLabels = useCallback(() => setOpenViewLabels(false), [])
  const handleOpenViewLabels = useCallback(() => {
    setOpenViewLabels(true)
  }, [])

  const handleOpenUpdateLabelInfo = useCallback(() => {
    setOpenUpdateLabelInfo(true)
  }, [])

  const handleSubmit = useCallback(
    async (data: LabelInformationFormData) => {
      setIsLoading(true)
      try {
        // Track if we need to wait for file propagation
        let hasFileUploads = false

        // First update product metadata
        const updateResult = await updateProduct({
          input: {
            productId: Number(productId),
            versionNumber: version,
            privateLabel: data.isPrivateLabel,
            labelPrintedBySupplier: data.isPrintedBySupplier,
            labelAppliedBySupplier: data.isAppliedBySupplier,
          },
        })

        if (updateResult.error || updateResult.data?.product.update.__typename === "ProductUpdateFailure") {
          const errorMessage =
            updateResult.data?.product.update.__typename === "ProductUpdateFailure"
              ? updateResult.data.product.update.error.message
              : "Unable to update product information. Please try again."
          throw new Error(errorMessage)
        }

        // Handle file uploads
        if (data.image) {
          // Upload image
          const uploadSuccess = await uploadImageFile(
            data.image,
            FieldGroupEnum.ProductLabel,
            productId.toString(),
            "supplier_label"
          )
          if (!uploadSuccess) {
            throw new Error(`Unable to upload image: ${data.image.name}. Please try again.`)
          }
          hasFileUploads = true
        } else if (Object.keys(data.pdfs).length > 0) {
          // Upload PDFs
          const uploadResults = await Promise.all(
            Object.entries(data.pdfs).map(async ([label, file]) => {
              const success = await uploadPDFFile(file, FieldGroupEnum.ProductLabel, label)
              return {
                success,
                label,
                fileName: file.name,
              }
            })
          )

          const failures = uploadResults.filter((result) => !result.success)
          if (failures.length > 0) {
            const failureMessages = failures.map((failure) => `Unable to upload ${failure.fileName}`).join("\n")
            throw new Error(`Some files could not be uploaded:\n${failureMessages}\nPlease try again.`)
          }

          hasFileUploads = uploadResults.length > 0
        }

        // Refresh product info after successful update
        getProductLabelInfo({ requestPolicy: "network-only" })

        // Only wait for S3 propagation if we uploaded files
        if (hasFileUploads) {
          // Wait for S3 propagation
          await new Promise((resolve) => setTimeout(resolve, 4000))

          // Verify files were uploaded successfully
          const fileInfoResult = await getFileInfo()
          const uploadedFiles = fileInfoResult.data?.fileUpload.getFileInfo

          if (uploadedFiles?.__typename === "GetFileInfoFailure" || !uploadedFiles) {
            throw new Error(
              "The product information was updated, but we couldn't verify the file uploads. Please check if the files appear correctly."
            )
          }
        }

        setOpenUpdateLabelInfo(false)
        setIsLoading(false)

        notify({
          message: hasFileUploads
            ? "Label information and files updated successfully"
            : "Label information updated successfully",
          type: NotificationType.Success,
        })
      } catch (error) {
        setIsLoading(false)
        notify({
          message: error instanceof Error ? error.message : "Unable to update label information. Please try again.",
          type: NotificationType.Error,
        })
      }
    },
    [
      updateProduct,
      productId,
      version,
      uploadPDFFile,
      uploadImageFile,
      getProductLabelInfo,
      getFileInfo,
      notify,
      setIsLoading,
      setOpenUpdateLabelInfo,
    ]
  )

  const handleViewLabelsSubmit = useCallback(
    (data: Pick<LabelInformationFormData, "pdfs">) => {
      handleSubmit({
        ...labelInfo,
        ...data,
        image: null,
      })
    },
    [handleSubmit, labelInfo]
  )

  return (
    <>
      <Paper className='border border-gray-400 p-6' elevation={0}>
        <Header expanded={expanded} onExpandClick={toggleExpanded} />
        <Collapse in={expanded}>
          {!hasFiles ? (
            <InformationMissingAlert
              title='Add Product Label Information'
              description=''
              {...(!isPastProductVersion && {
                action: (
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    className='text-primary'
                    startIcon={<AddOutlined />}
                    onClick={handleOpenUpdateLabelInfo}
                    data-testid='add-label-info-button'
                  >
                    Labeling Info
                  </Button>
                ),
              })}
              data-testid='product-label-info-alert'
            />
          ) : (
            <LabelInformationContent
              labelInfo={labelInfo}
              hasFiles={hasFiles}
              isPastProductVersion={isPastProductVersion}
              onEdit={handleOpenUpdateLabelInfo}
              onViewLabels={handleOpenViewLabels}
            />
          )}
        </Collapse>
      </Paper>

      {openUpdateLabelInfo && (
        <LabelInformationDialog
          onClose={handleCloseUpdateLabelInfo}
          onSave={handleSubmit}
          pdfFiles={files.pdfs}
          imageFile={files.image}
          labelInfo={labelInfo}
          isLoading={isLoading}
          onRemove={removeSingleFile}
        />
      )}

      <ViewLabelsDialog
        open={openViewLabels}
        onClose={handleCloseViewLabels}
        onSave={handleViewLabelsSubmit}
        pdfFiles={files.pdfs}
        imageFile={files.image}
        labelInfo={labelInfo}
      />
    </>
  )
}
